import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, money } from "@/lib/api";

export const DeleteInvoiceButton = ({ inv, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const paid = inv.status === "paid";
  const ok = !paid || typed.trim() === inv.number;
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/invoices/${inv.id}`, { params: paid ? { confirm: typed.trim() } : {} });
      toast.success(`${inv.number} deleted`); setOpen(false); onDeleted();
    } catch (e) { toast.error(errMsg(e, "Could not delete invoice")); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="ghost" data-testid={`delete-invoice-${inv.id}`} onClick={() => { setTyped(""); setOpen(true); }} className="h-8 rounded-full text-slate-300 hover:text-rose-400 hover:bg-rose-500/10">
        <Trash2 className="h-4 w-4 mr-1" />Delete</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="delete-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Delete {inv.number}?</DialogTitle>
            <DialogDescription className="text-slate-400">{inv.client?.name || "No client"} · {money(inv.amount, inv.currency)} · {inv.status}. This can't be undone.</DialogDescription>
          </DialogHeader>
          {paid && (
            <div className="space-y-3">
              <p className="flex gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200" data-testid="delete-invoice-paid-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />This invoice is paid. Deleting it removes the payment from revenue totals and locks the study in the client portal unless another paid invoice covers it. The deletion is recorded in the activity log.</p>
              <label className="text-sm text-slate-300">Type <span className="num font-semibold text-slate-100">{inv.number}</span> to confirm</label>
              <Input data-testid="delete-invoice-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} className="bg-slate-950/60 border-slate-700 num" autoFocus />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="delete-invoice-cancel">Cancel</Button>
            <Button data-testid="delete-invoice-confirm-btn" disabled={!ok || busy} onClick={remove} className="rounded-full bg-rose-500 text-white hover:bg-rose-400">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Delete invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
